// @flow
import React from "react";

const Markdown = (props: { text: string }) => {
    const { text } = props;
    if (!text) return null;
    // Split the text into lines for processing
    const lines = text.split("\n");
    const result = [];
    let tableRows = [];
    let inTable = false;
    let key = 0;
    let list_stack = []; // Stack to manage nested lists
    let list_indentations = [];

    const finishTable = () => {
        if (inTable) {
            inTable = false;
            result.push(
                <table key={`table-${key}`} className="w-full border-collapse border border-gray-300 mt-4 mb-4">
                    <tbody>{tableRows}</tbody>
                </table>
            );
        }
    };

    const finishList = () => {
        while (list_stack.length > 0) {
            const items = list_stack.pop();
            const item = (
                <ul key={key} className="pl-3">
                    {items}
                </ul>
            );
            if (list_stack.length === 0) {
                result.push(item);
            } else {
                list_stack[list_stack.length - 1].push(<li key={key++}>{item}</li>);
            }
        }
    };

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        key++;

        // Handle headings (# Heading)
        if (line.match(/^#{1,6}\s/)) {
            finishList();
            finishTable();

            const level = line.match(/^(#+)/)?.[0]?.length || 0;
            const content = line.replace(/^#+\s/, "");
            const HeadingTag = `h${level}`;
            result.push(
                <HeadingTag key={key} className="font-bold mt-4 mb-2">
                    <MarkdownInlineElements text={content} />
                </HeadingTag>
            );
        }
        // Handle bullet lists (*, -, +)
        else if (line.match(/^\s*[*\-+]\s/)) {
            finishTable();

            const content = line.replace(/^\s*[*\-+]\s/, "");
            const current_level = (line.match(/^\s*/) || [""])[0].length; // Determine the current indentation level

            // Create a new list if non exists atm
            let active_list = list_stack[list_stack.length - 1];
            if (active_list !== undefined) {
                let active_indentation = list_indentations[list_indentations.length - 1];
                if (active_indentation > current_level) {
                    // current line is less indented than the active list
                    while (active_indentation > current_level) {
                        const items = list_stack.pop();
                        if (list_stack.length === 0) {
                            list_stack = [[]];
                        }
                        active_list = list_stack[list_stack.length - 1];
                        if (active_indentation === 0) {
                            active_list.push(
                                <ul key={key} className="pl-3">
                                    {items}
                                </ul>
                            );
                        } else {
                            const new_list = (
                                <ul key={key} className="pl-3">
                                    {items}
                                </ul>
                            );
                            if (active_list.length === 0) {
                                // just add the list item
                                active_list.push(<li key={key}>{new_list}</li>);
                            } else {
                                // add the list to the content of the previous list item
                                const prev_item = active_list[active_list.length - 1];
                                const new_children = React.Children.toArray(prev_item.props.children).concat(new_list);
                                active_list[active_list.length - 1] = <li key={prev_item.key}>{new_children}</li>;
                            }
                        }
                        list_indentations.pop();
                        active_indentation = list_indentations[list_indentations.length - 1];
                    }
                } else if (active_indentation < current_level) {
                    // current line is more indented than the active list
                    active_list = [];
                    list_stack.push(active_list);
                    list_indentations.push(current_level);
                }
            }
            if (active_list === null || active_list === undefined) {
                active_list = [];
                list_stack = [active_list];
                list_indentations = [current_level];
            }

            // Add the list item to the current list
            const list_item = (
                <li key={key} className="">
                    <MarkdownInlineElements text={content} />
                </li>
            );

            active_list.push(list_item);
        }
        // Handle table rows
        else if (line.match(/^\|.*\|$/)) {
            finishList();

            if (!inTable) {
                inTable = true;
                tableRows = [];
            }

            // Check if it's a separator row
            if (line.match(/^\|\s*[-:]+[-|\s:]*\|$/)) {
                continue; // Skip separator rows
            }

            // Process table row
            const cells = line
                .split("|")
                .filter(cell => cell) // Remove empty cells from start/end
                .map(cell => cell.trim());

            tableRows.push(
                <tr key={`row-${key}`} className="border-b border-gray-300">
                    {cells.map((cell, cellIndex) => (
                        <td key={`cell-${key}-${cellIndex}`} className="px-4 py-2">
                            <MarkdownInlineElements text={cell} />
                        </td>
                    ))}
                </tr>
            );
        }
        // If line is empty and we were in a list, finish the list
        else if (line.trim() === "") {
            finishList();
            finishTable();
        }
        // Regular paragraph
        else if (line.trim() !== "") {
            finishList();
            finishTable();

            result.push(
                <p key={key} className="mb-4">
                    <MarkdownInlineElements text={line} />
                </p>
            );
        }
    }

    finishTable();
    finishList();

    return result;
};

const MarkdownInlineElements = (props: { text: string }) => {
    const { text } = props;
    if (!text) return null;

    // Array to collect the parts
    const parts = [];
    let lastIndex = 0;
    let currentKey = 0;

    // Process strong/bold text
    const processText = processed => {
        // Process links [text](url)
        let linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
        let linkMatch;
        let lastLinkIndex = 0;
        let linkParts = [];

        while ((linkMatch = linkRegex.exec(processed)) !== null) {
            if (linkMatch === null) {
                break;
            }
            // Add text before the link
            if (linkMatch.index > lastLinkIndex) {
                linkParts.push(processed.substring(lastLinkIndex, linkMatch.index));
            }

            // Add the link
            linkParts.push(
                <a
                    key={`link-${currentKey++}`}
                    href={linkMatch[2]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                >
                    {linkMatch[1]}
                </a>
            );

            lastLinkIndex = linkMatch.index + linkMatch[0].length;
        }

        // Add any remaining text
        if (lastLinkIndex < processed.length) {
            linkParts.push(processed.substring(lastLinkIndex));
        }

        return linkParts.length === 1 ? linkParts[0] : linkParts;
    };

    // Parse bold text (**text** or __text__)
    let boldRegex = /(\*\*)([^**]+?)\1/g;
    let boldMatch;

    while ((boldMatch = boldRegex.exec(text)) !== null) {
        if (boldMatch === null) {
            break;
        }
        // Add text before the bold
        if (boldMatch.index > lastIndex) {
            parts.push(processText(text.substring(lastIndex, boldMatch.index)));
        }

        // Add the bold text
        parts.push(<strong key={`bold-${currentKey++}`}>{processText(boldMatch[2])}</strong>);

        lastIndex = boldMatch.index + boldMatch[0].length;
    }

    // Add remaining text after bold processing
    let remainingText = text.substring(lastIndex);

    // Parse italic text (*text* or _text_)
    let italicRegex = /(\*|_)([^*_]+?)\1/g;
    let italicMatch;

    while ((italicMatch = italicRegex.exec(remainingText)) !== null) {
        if (italicMatch === null) {
            break;
        }
        // Add text before the italic
        if (italicMatch.index > 0) {
            parts.push(processText(remainingText.substring(0, italicMatch.index)));
        }

        // Add the italic text
        parts.push(<em key={`italic-${currentKey++}`}>{processText(italicMatch[2])}</em>);

        // Update remaining text to exclude processed italic text
        remainingText = remainingText.substring(italicMatch.index + italicMatch[0].length);
    }

    // Add any remaining text after italic processing
    if (remainingText.length > 0) {
        parts.push(processText(remainingText));
    }

    return parts.length === 0 ? text : parts;
};

// Function to parse markdown text into React components
const SimpleMarkdownRenderer = (props: { markdown: string }) => {
    const { markdown } = props;

    return (
        <div className="markdown-renderer">
            <Markdown text={markdown} />
        </div>
    );
};

export default SimpleMarkdownRenderer;
